import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import {
  Bell,
  CheckCircle2,
  AlertCircle,
  Info,
  XCircle,
  Trash2,
  Check,
  Filter,
  Clock,
  ChevronDown,
  ChevronUp,
  Database,
  Server,
  Building2,
} from 'lucide-react';
import { alertsService, observabilityService } from '../services';
import type { Notification as NotificationAPI } from '../services';
import tenantService from '../services/tenant.service';
import { useAuthStore } from '../stores/authStore';
import { useSettingsStore } from '../stores/settingsStore';
import { useTenantStore } from '../stores/tenantStore';
import { useToast } from '../components/common/ToastContainer';
import { USER_TYPES } from '../utils/constants';
import { ChevronFirst, ChevronLeft as ChevronLeftNav, ChevronRight as ChevronRightNav, ChevronLast } from 'lucide-react';

interface Notification extends Omit<NotificationAPI, 'created_at' | 'read'> {
  timestamp: Date;
  read: boolean;
}

interface Edge {
  id: string;
  name: string;
  _internalId?: string;
  type?: 'edge' | 'mtge';
}

const Notifications = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { canDelete } = usePermissions();
  const { getTenantId, setSelectedTenantId } = useTenantStore();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [tenantMap, setTenantMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterRead, setFilterRead] = useState<string>('all');
  const [filterDevice, setFilterDevice] = useState<string>('all');
  // VSNP-684: source filter — alert rows from the notifications table
  // OR synthesised config-sync rows from edge state. Default 'all' so
  // /notifications becomes the unified drill-down for the dashboard
  // "Recent Activity" widget.
  const [filterSource, setFilterSource] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuthStore();
  const { tablePageSize, setTablePageSize } = useSettingsStore();
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;

  useEffect(() => {
    fetchNotifications();
    fetchEdges();
    if (isSuperAdmin) fetchTenants();
  }, []);

  const fetchEdges = async () => {
    try {
      const res = await observabilityService.getRegisteredEdges();
      if (res.status === 'success' && res.data && res.data.length > 0) {
        setEdges(res.data.sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (error) {
      console.error('Failed to fetch edges:', error);
    }
  };

  const fetchTenants = async () => {
    try {
      const tenants = await tenantService.getSelect();
      const map: Record<string, string> = {};
      tenants.forEach((t) => { map[t.id] = t.name; });
      setTenantMap(map);
    } catch {
      // Non-critical — tenant names just won't resolve
    }
  };

  const getTenantName = (tenantId: string) => {
    return tenantMap[tenantId] || tenantId.slice(0, 12) + '...';
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await alertsService.getNotifications({ limit: 100 });

      const transformed: Notification[] = data.map((n) => ({
        ...n,
        timestamp: new Date(n.created_at),
        read: n.read === 1,
        category: n.category || 'System',
      }));

      setNotifications(transformed);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-success-600" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-warning-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-danger-600" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-info-600" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'border-l-success-500 bg-success-50/30';
      case 'warning':
        return 'border-l-warning-500 bg-warning-50/30';
      case 'error':
        return 'border-l-danger-500 bg-danger-50/30';
      case 'info':
      default:
        return 'border-l-info-500 bg-info-50/30';
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await alertsService.markNotificationAsRead(id);
      setNotifications(
        notifications.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter((n) => !n.read);
      await Promise.all(
        unreadNotifications.map((n) => alertsService.markNotificationAsRead(n.id))
      );
      setNotifications(notifications.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await alertsService.deleteNotification(id);
      setNotifications(notifications.filter((n) => n.id !== id));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const clearAll = async () => {
    try {
      await Promise.all(
        notifications.map((n) => alertsService.deleteNotification(n.id))
      );
      setNotifications([]);
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
    }
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Resolve edge info from the fetched edges list
  const getEdgeInfo = (edgeId: string) => {
    const edge = edges.find((e) => e.id === edgeId);
    if (!edge) return { name: edgeId.slice(0, 8) + '...', link: null, type: null as string | null };
    const prefix = edge.type === 'mtge' ? '/mtges' : '/edges';
    const linkId = edge._internalId || edge.id;
    return { name: edge.name, link: `${prefix}/${linkId}`, type: edge.type || 'edge' };
  };

  // Navigate to device, auto-switching tenant context if needed
  const navigateToDevice = (link: string, type: string | null, tenantId: string | null) => {
    // MTGEs don't need tenant context (SuperAdminGuard)
    if (type === 'mtge') {
      navigate(link);
      return;
    }

    // If a tenant is already selected, just navigate
    if (getTenantId()) {
      navigate(link);
      return;
    }

    // SuperAdmin with "All Tenants" — need to switch tenant first
    if (!tenantId) {
      toast.warning('Cannot Navigate', 'This device has no associated tenant. Select a tenant first.');
      return;
    }

    const tenantName = tenantMap[tenantId] || tenantId;
    setSelectedTenantId(tenantId);
    toast.info('Tenant Switched', `Switched to "${tenantName}"`);
    // Small delay to let the store update propagate before navigation
    setTimeout(() => navigate(link), 50);
  };

  const filteredNotifications = notifications.filter((n) => {
    if (filterType !== 'all' && n.type !== filterType) return false;
    // Source filter: 'alert' rows have source==='alert' (or source missing,
    // for legacy rows pre-VSNP-684); 'config_sync' rows are explicitly
    // tagged. Default 'all' returns both.
    const rowSource = (n as any).source || 'alert';
    if (filterSource !== 'all' && rowSource !== filterSource) return false;
    if (filterRead === 'unread' && n.read) return false;
    if (filterRead === 'read' && !n.read) return false;
    if (filterDevice !== 'all') {
      if (filterDevice === 'system') {
        if (n.edge_id) return false;
      } else {
        if (n.edge_id !== filterDevice) return false;
      }
    }
    return true;
  });

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [filterType, filterRead, filterDevice, filterSource]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredNotifications.length / tablePageSize));
  const activePage = Math.min(currentPage, totalPages);
  const pageStart = (activePage - 1) * tablePageSize;
  const paginatedNotifications = filteredNotifications.slice(pageStart, pageStart + tablePageSize);

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Notifications</h1>
            <p className="page-subtitle">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4 mr-2 inline" />
              Mark All Read
            </button>
            {canDelete && (
              <button
                onClick={clearAll}
                disabled={notifications.length === 0}
                className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4 mr-2 inline" />
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-secondary-500" />
            <span className="text-sm font-heading text-gray-700">Filter:</span>
          </div>

          {/* Source Filter — alerts vs config-sync events (VSNP-684) */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600 font-sans">Source:</label>
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="input-base w-44 text-sm"
            >
              <option value="all">All Sources</option>
              <option value="alert">Alerts</option>
              <option value="config_sync">Config Changes</option>
            </select>
          </div>

          {/* Type Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600 font-sans">Severity:</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="input-base w-40 text-sm"
            >
              <option value="all">All Severities</option>
              <option value="error">Errors</option>
              <option value="warning">Warnings</option>
              <option value="success">Success</option>
              <option value="info">Info</option>
            </select>
          </div>

          {/* Read Status Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600 font-sans">Status:</label>
            <select
              value={filterRead}
              onChange={(e) => setFilterRead(e.target.value)}
              className="input-base w-40 text-sm"
            >
              <option value="all">All</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
            </select>
          </div>

          {/* Device Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600 font-sans">Device:</label>
            <select
              value={filterDevice}
              onChange={(e) => setFilterDevice(e.target.value)}
              className="input-base w-56 text-sm"
            >
              <option value="all">All Devices</option>
              <option value="system">System (no device)</option>
              {edges.map((edge) => (
                <option key={edge.id} value={edge.id}>
                  {edge.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {paginatedNotifications.length === 0 ? (
          <div className="card text-center py-12">
            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-heading-md text-gray-500 mb-2">No notifications</h3>
            <p className="text-sm text-gray-400 font-sans">
              You're all caught up! No new notifications to display.
            </p>
          </div>
        ) : (
          paginatedNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`card border-l-4 ${getTypeColor(notification.type)} ${!notification.read ? 'border-2 border-gray-200' : ''
                } transition-all duration-200 hover:border-gray-300`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="flex-shrink-0 mt-1">
                    {getIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-heading-sm text-gray-900">
                        {notification.title}
                      </h3>
                      {/* Unread dot only for alerts — config-sync rows have no read/unread state (VSNP-684) */}
                      {!notification.read && (notification as any).source !== 'config_sync' && (
                        <span className="w-2 h-2 bg-primary-900 rounded-full"></span>
                      )}
                      {(notification as any).source === 'config_sync' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-heading text-blue-700 bg-blue-100">
                          Config Change
                        </span>
                      )}
                      {notification.category && (notification as any).source !== 'config_sync' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-heading text-secondary-700 bg-secondary-100">
                          {notification.category}
                        </span>
                      )}
                      {notification.resolved === 1 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-heading text-success-700 bg-success-100">
                          Resolved
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 font-sans mb-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center space-x-3 text-xs text-gray-500 font-sans">
                      <div className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatTimestamp(notification.timestamp)}
                      </div>
                      {notification.edge_id && (() => {
                        const info = getEdgeInfo(notification.edge_id);
                        return (
                          <div className="flex items-center">
                            <Server className="w-3 h-3 mr-1 text-primary-500" />
                            {info.link ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); navigateToDevice(info.link!, info.type, notification.tenant_id); }}
                                className="text-primary-600 hover:text-primary-800 hover:underline"
                              >
                                {info.name}
                              </button>
                            ) : (
                              <span>{info.name}</span>
                            )}
                          </div>
                        );
                      })()}
                      {isSuperAdmin && notification.tenant_id && (
                        <div className="flex items-center">
                          <Building2 className="w-3 h-3 mr-1 text-purple-500" />
                          <span className="text-purple-700">{getTenantName(notification.tenant_id)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => toggleExpand(notification.id)}
                    className="icon-button text-primary-600 hover:bg-primary-50"
                    title={expandedId === notification.id ? "Hide details" : "Show details"}
                  >
                    {expandedId === notification.id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  {/* Mark-as-read + Delete are alert-only actions — config-sync
                      rows are derived state and have no DB row to mutate (VSNP-684) */}
                  {(notification as any).source !== 'config_sync' && !notification.read && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="icon-button-primary"
                      title="Mark as read"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  {(notification as any).source !== 'config_sync' && canDelete && (
                    <button
                      onClick={() => deleteNotification(notification.id)}
                      className="icon-button text-danger-600 hover:bg-danger-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Expansion Details */}
              {expandedId === notification.id && (
                <div className="mt-4 pt-4 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {notification.edge_id && (() => {
                      const info = getEdgeInfo(notification.edge_id);
                      return (
                        <div className="flex items-center text-sm text-gray-600">
                          <Database className="w-4 h-4 mr-2 text-primary-500" />
                          <span className="font-medium mr-2">Device:</span>
                          {info.link ? (
                            <button
                              onClick={() => navigateToDevice(info.link!, info.type, notification.tenant_id)}
                              className="text-primary-600 hover:text-primary-800 hover:underline font-medium mr-2"
                            >
                              {info.name}
                            </button>
                          ) : (
                            <span className="mr-2">{info.name}</span>
                          )}
                          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{notification.edge_id}</code>
                        </div>
                      );
                    })()}
                    {notification.tenant_id && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Building2 className="w-4 h-4 mr-2 text-purple-500" />
                        <span className="font-medium mr-2">Tenant:</span>
                        <span className="text-purple-700 font-medium mr-2">{getTenantName(notification.tenant_id)}</span>
                        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{notification.tenant_id}</code>
                      </div>
                    )}
                  </div>

                  {notification.metadata && Object.keys(notification.metadata).length > 0 && (
                    <div className="space-y-2 mb-4">
                      <div className="text-xs font-heading font-semibold text-gray-500 uppercase tracking-wider">
                        Metadata Details
                      </div>
                      <pre className="text-xs text-secondary-800 bg-secondary-50/50 p-4 rounded-lg border border-secondary-100 overflow-x-auto font-mono leading-relaxed">
                        {JSON.stringify(notification.metadata, null, 2)}
                      </pre>
                    </div>
                  )}

                  {notification.alert_fingerprint && (
                    <div className="flex items-center mt-4 pt-4 border-t border-gray-50 text-xs text-gray-500">
                      <Bell className="w-3.5 h-3.5 mr-2 text-primary-400" />
                      <span className="font-medium mr-2">Alert Fingerprint:</span>
                      <code className="bg-gray-50 px-1.5 py-0.5 rounded font-mono">{notification.alert_fingerprint}</code>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {filteredNotifications.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600 px-2 mt-4">
          {totalPages > 1 ? (
            <div className="flex items-center space-x-1">
              <button onClick={() => setCurrentPage(1)} disabled={activePage <= 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="First page">
                <ChevronFirst className="w-4 h-4" />
              </button>
              <button onClick={() => setCurrentPage(activePage - 1)} disabled={activePage <= 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="Previous page">
                <ChevronLeftNav className="w-4 h-4" />
              </button>
              <span className="px-2">Page {activePage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(activePage + 1)} disabled={activePage >= totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="Next page">
                <ChevronRightNav className="w-4 h-4" />
              </button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={activePage >= totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="Last page">
                <ChevronLast className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <span />
          )}
          <div className="flex items-center space-x-3">
            <span>
              Showing {pageStart + 1}–{Math.min(pageStart + tablePageSize, filteredNotifications.length)} of {filteredNotifications.length}
            </span>
            <select
              value={tablePageSize}
              onChange={(e) => { setTablePageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white"
            >
              {[10, 25, 50, 100].map((opt) => (
                <option key={opt} value={opt}>{opt} / page</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
